import {
  PLATE_BARE_GROUND_EDGE_COLOR,
  PLATE_CLARET,
  PLATE_COUNTRY_DASH_OPACITY,
  PLATE_COUNTRY_RIBBON_OPACITY,
  PLATE_COUNTRY_TINT_OPACITY,
  PLATE_KEY_RIBBON_WIDTHS,
  PLATE_LAND_COLOR,
  PLATE_PAPER_COLOR,
  PLATE_RIBBON_LAYER_OPACITY,
  PLATE_RIBBON_TINTS,
  PLATE_SELECTED_LINE_COLOR,
  PLATE_SELECTED_RIBBON_COLOR,
  PLATE_VINEYARD_COLOR,
  PLATE_VINEYARD_EDGE_COLOR,
} from '../../lib/chrome_config'
import {
  COVERAGE_DRAWN,
  COVERAGE_NAMED,
  SELECTED_GLOW_OPACITY,
  SIZE_CLASSES,
  TASTING_PIN_COLOR,
  TASTING_PIN_RING_COLOR,
  WINERY_CLUSTER_COLOR,
  WINERY_CLUSTER_LABEL_COLOR,
  WINERY_DERIVED_POINT_OPACITY,
  WINERY_SIZE_COLORS,
  WINERY_STROKE_COLOR,
  WINERY_UNKNOWN_COLOR,
} from '../../lib/map_config'

/*
 * THE MAP'S OWN MARKS, AT THE SIZE OF A KEY.
 *
 * One set of swatches for the three places the chrome draws them: the plate
 * ribbon along the bottom of the map, the full key in the Layers popover, and
 * the mark in a panel's title bar. They were inline spans in the popover, each
 * surface free to draw "an appellation" its own way; three surfaces drawing
 * one mark three ways is three keys.
 *
 * Every swatch is the mark as the map draws it, built the way the map builds
 * it: the land colour underneath, the tint over it at the opacity the layer
 * has, and the edge on top at full strength. A swatch of 11% claret on the
 * popover's paper would be a square nobody can see; over the land, inside its
 * own hairline, it is the thing on the map.
 *
 * THE SHAPE IS A CONTRACT WITH THE TESTS. The root span of each swatch is
 * `aria-hidden` and carries the EDGE (as its border); what the edge encloses —
 * the ribbon, the tint — is on the span or spans inside it, so an opacity
 * fades the inside and never the edge. `layer_toggles.test.tsx` reads those
 * two levels against the constants the map paints with.
 */

// A 20x14 patch: wide enough for a ribbon inside a hairline to leave a clean
// centre at every depth, and no taller than a line of 11px text.
const PATCH = 'relative inline-block h-3.5 w-5 shrink-0 overflow-hidden rounded-[3px]'

// What fills a patch edge to edge, under or inside its border.
const INSIDE = 'absolute inset-0 rounded-[2px]'

/**
 * An appellation at one depth of nesting: the claret hairline, and inside it
 * the ribbon that narrows and deepens with each level. The ground inside is
 * left clean, which is the point of the drawing.
 */
export function RibbonSwatch({ depth = 0 }: { depth?: number }) {
  const level = Math.min(Math.max(depth, 0), PLATE_RIBBON_TINTS.length - 1)

  return (
    <span
      aria-hidden="true"
      data-swatch="ribbon"
      className={`${PATCH} border`}
      style={{ borderColor: PLATE_CLARET, backgroundColor: PLATE_LAND_COLOR }}
    >
      <span
        className={INSIDE}
        style={{
          borderWidth: PLATE_KEY_RIBBON_WIDTHS[level],
          borderColor: PLATE_RIBBON_TINTS[level],
          opacity: PLATE_RIBBON_LAYER_OPACITY,
        }}
      />
    </span>
  )
}

/**
 * The place a panel describes: the amber ribbon in place of the claret one, a
 * darker line outside it and a paper casing outside that. Opaque, because the
 * selection is the one mark on the plate that is.
 */
export function SelectedSwatch({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-swatch="selected"
      className={`${PATCH} border-[1.5px] ${className}`}
      style={{
        borderColor: PLATE_SELECTED_LINE_COLOR,
        backgroundColor: PLATE_LAND_COLOR,
        boxShadow: `0 0 0 1px ${PLATE_PAPER_COLOR}`,
      }}
    >
      <span className={INSIDE} style={{ borderWidth: 3, borderColor: PLATE_SELECTED_RIBBON_COLOR }} />
    </span>
  )
}

/**
 * A COUNTRY a panel describes. Not the ribbon above: a country's outline is
 * simplified to about 22 km, and a crisp cased line along it would trace that
 * error across every bay, so the map draws a soft amber band inside the coast
 * instead (`SELECTED_GLOW_OPACITY` in map_config.ts). The key draws what the
 * map draws — an edge that fades inward, no hard line — because a key that
 * showed the appellation's ribbon here was describing a mark that was not on
 * the map.
 */
export function SelectedCountrySwatch({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-swatch="selected-country"
      className={`${PATCH} ${className}`}
      style={{
        backgroundColor: PLATE_LAND_COLOR,
        boxShadow: `inset 0 0 0 1px ${PLATE_SELECTED_RIBBON_COLOR}, inset 0 0 3px 2.5px ${PLATE_SELECTED_RIBBON_COLOR}`,
        opacity: SELECTED_GLOW_OPACITY,
      }}
    />
  )
}

/**
 * A country, by what the atlas holds for it (`AtlasFeatureProperties.coverage`).
 * The three differ in KIND of mark and not only in strength: a tint with a
 * solid ribbon, a fainter tint with a dashed line, and bare ground.
 */
export function CountrySwatch({ coverage }: { coverage: string }) {
  if (coverage === COVERAGE_DRAWN) {
    return (
      <span aria-hidden="true" data-swatch="country" className={PATCH} style={{ backgroundColor: PLATE_LAND_COLOR }}>
        <span
          className={INSIDE}
          style={{ backgroundColor: PLATE_CLARET, opacity: PLATE_COUNTRY_TINT_OPACITY.drawn }}
        />
        <span
          className={INSIDE}
          style={{ borderWidth: 2, borderColor: PLATE_CLARET, opacity: PLATE_COUNTRY_RIBBON_OPACITY }}
        />
      </span>
    )
  }

  if (coverage === COVERAGE_NAMED) {
    return (
      <span aria-hidden="true" data-swatch="country" className={PATCH} style={{ backgroundColor: PLATE_LAND_COLOR }}>
        <span
          className={INSIDE}
          style={{ backgroundColor: PLATE_CLARET, opacity: PLATE_COUNTRY_TINT_OPACITY.named }}
        />
        <span
          className={`${INSIDE} border-dashed`}
          style={{ borderWidth: 1, borderColor: PLATE_CLARET, opacity: PLATE_COUNTRY_DASH_OPACITY }}
        />
      </span>
    )
  }

  // Nothing imported: bare ground. The border is the key's, not the map's — a
  // patch of land colour with no edge is not a patch.
  return (
    <span
      aria-hidden="true"
      data-swatch="country"
      className={`${PATCH} border`}
      style={{ borderColor: PLATE_BARE_GROUND_EDGE_COLOR, backgroundColor: PLATE_LAND_COLOR }}
    />
  )
}

/** The vines the basemap draws on the ground, with the edge it gives them close in. */
export function VineyardSwatch() {
  return (
    <span
      aria-hidden="true"
      data-swatch="vineyard"
      className={`${PATCH} border`}
      style={{ borderColor: PLATE_VINEYARD_EDGE_COLOR, backgroundColor: PLATE_VINEYARD_COLOR }}
    />
  )
}

interface WineryDotProps {
  color: string
  /** The fill's strength: an estimated class is drawn fainter than a stated one. */
  opacity?: number
  /** The dot a panel describes: it keeps its colour and gains the amber ring and halo. */
  selected?: boolean
  className?: string
}

/**
 * A winery as the map draws one: a dot in its size class's colour inside a
 * paper ring. The ring is a shadow rather than a border, so every dot is the
 * same 10px whatever is round it.
 */
export function WineryDot({ color, opacity, selected = false, className = '' }: WineryDotProps) {
  return (
    <span
      aria-hidden="true"
      data-swatch="winery"
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${className}`}
      style={{
        backgroundColor: color,
        opacity,
        boxShadow: selected
          ? `0 0 0 2.5px ${PLATE_SELECTED_RIBBON_COLOR}, 0 0 0 5.5px ${PLATE_SELECTED_RIBBON_COLOR}4d`
          : `0 0 0 1.5px ${WINERY_STROKE_COLOR}, 0 0 0 2px rgb(42 13 22 / 0.14)`,
      }}
    />
  )
}

/**
 * ONE producer's dot, as the winery layer paints it (`winerySizeColor` and
 * `winerySizeOpacity` in winery_layer.ts, which are MapLibre expressions and
 * cannot be called): its size class's colour, fainter when the class is an
 * estimate and not a stated figure, and the neutral when it has none.
 *
 * Here because two panels draw that dot and must draw the same one — the
 * producer's own, and the stand-in shown for it while its page is on the way.
 */
export function wineryDotPaint(sizeClass: string | null | undefined, stated: boolean): { color: string; opacity?: number } {
  if (!sizeClass) return { color: WINERY_UNKNOWN_COLOR }

  return {
    color: WINERY_SIZE_COLORS[sizeClass] ?? WINERY_UNKNOWN_COLOR,
    opacity: stated ? undefined : WINERY_DERIVED_POINT_OPACITY,
  }
}

/** The four size classes side by side, smallest first: the ramp, as one mark. */
export function WinerySizeRamp() {
  return (
    <span aria-hidden="true" data-swatch="ramp" className="inline-flex shrink-0 items-center gap-1">
      {SIZE_CLASSES.map((sizeClass) => (
        <WineryDot key={sizeClass} color={WINERY_SIZE_COLORS[sizeClass]} />
      ))}
    </span>
  )
}

/**
 * A cluster: a paper chip with a teal ring and a count, never a solid teal
 * disc, which would read as a size class. The count is an example — what the
 * chip keys is that the number is how many wineries are nearby — so it is
 * generated content: part of the drawing, and not a "12" in the text of every
 * row that keys a cluster.
 */
export function ClusterChip() {
  return (
    <span
      aria-hidden="true"
      data-swatch="cluster"
      className="inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full font-display text-[9px] font-semibold leading-none before:content-['12']"
      style={{
        backgroundColor: WINERY_STROKE_COLOR,
        color: WINERY_CLUSTER_LABEL_COLOR,
        boxShadow: `inset 0 0 0 1.5px ${WINERY_CLUSTER_COLOR}`,
      }}
    />
  )
}

/*
 * THE READER'S OWN MARKS (`TASTING_PIN_COLOR` in map_config.ts has why they are
 * claret discs, and why each has a shape of its own).
 */

/** One tasting: a claret core, a paper ring, a claret ring. No winery dot has a second ring. */
export function TastingPin({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-swatch="tasting"
      className={`inline-block h-[5px] w-[5px] shrink-0 rounded-full ${className}`}
      style={{
        backgroundColor: TASTING_PIN_COLOR,
        boxShadow: `0 0 0 2px ${TASTING_PIN_RING_COLOR}, 0 0 0 3.5px ${TASTING_PIN_COLOR}`,
      }}
    />
  )
}

/**
 * Several tastings in one place: a claret disc with their number in paper —
 * or, for wines the atlas can place no nearer than their COUNTRY, the same
 * disc inside out. The number is an example, and generated content for the
 * reason the cluster chip's is.
 */
export function TastingCount({ country = false }: { country?: boolean }) {
  return (
    <span
      aria-hidden="true"
      data-swatch={country ? 'tasting-country' : 'tasting-count'}
      className="inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full font-display text-[9px] font-semibold leading-none before:content-['3']"
      style={
        country
          ? {
              backgroundColor: TASTING_PIN_RING_COLOR,
              color: TASTING_PIN_COLOR,
              boxShadow: `inset 0 0 0 1.5px ${TASTING_PIN_COLOR}`,
            }
          : {
              backgroundColor: TASTING_PIN_COLOR,
              color: TASTING_PIN_RING_COLOR,
              boxShadow: `0 0 0 1.5px ${TASTING_PIN_RING_COLOR}, 0 0 0 2px rgb(42 13 22 / 0.14)`,
            }
      }
    />
  )
}

/**
 * The reader's mark on a winery they have wines from: a claret dot on the
 * winery's own dot, and beside it a small badge with how many
 * (`WINERY_MARK_MIN_ZOOM` in map_config.ts). Two discs in one box, the badge up
 * and to the right of the dot as the map draws it. The number is an example,
 * and generated content for the reason the cluster chip's is.
 */
export function TastingWinery() {
  return (
    <span aria-hidden="true" data-swatch="tasting-winery" className="relative inline-block h-3.5 w-[18px] shrink-0">
      <span
        className="absolute bottom-[1.5px] left-[1.5px] h-[7px] w-[7px] rounded-full"
        style={{ backgroundColor: TASTING_PIN_COLOR, boxShadow: `0 0 0 1.5px ${TASTING_PIN_RING_COLOR}` }}
      />
      <span
        className="absolute right-0 top-0 grid h-[11px] w-[11px] place-items-center rounded-full font-display text-[7.5px] font-semibold leading-none before:content-['3']"
        style={{
          backgroundColor: TASTING_PIN_COLOR,
          color: TASTING_PIN_RING_COLOR,
          boxShadow: `0 0 0 1px ${TASTING_PIN_RING_COLOR}`,
        }}
      />
    </span>
  )
}

/** A wish: the ring alone. Wanted, not yet drunk. */
export function WishlistPin() {
  return (
    <span
      aria-hidden="true"
      data-swatch="wishlist"
      className="inline-block h-3 w-3 shrink-0 rounded-full border-2"
      style={{ borderColor: TASTING_PIN_COLOR, backgroundColor: 'rgba(247,246,245,0.6)' }}
    />
  )
}
